import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// Async thunk for fetching equipment data
export const fetchEquipmentData = createAsyncThunk(
  "equipment/fetchEquipmentData",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "list");
      body.append("page", page);
      body.append("limit", limit);

      const response = await fetch(baseUrl + "equipment_master.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();

      if (!response.ok) {
        throw new Error(res.message || 'Failed to fetch equipment data');
      }

      if (!res.success) {
        return rejectWithValue(res.message || 'API returned unsuccessful response');
      }
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching equipment details
export const fetchEquipmentDetails = createAsyncThunk(
  "equipment/fetchEquipmentDetails",
  async (id, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "details");
      body.append("id", id);

      const response = await fetch(baseUrl + "equipment_master.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();

      if (!response.ok) {
        throw new Error(res.message || 'Failed to fetch equipment details');
      }

      if (!res.success) {
        return rejectWithValue(res.message || 'API returned unsuccessful response');
      }

      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating individual product
export const updateEquipmentProduct = createAsyncThunk(
  "equipment/updateEquipmentProduct",
  async ({ productId, updates }, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "update_product");
      body.append("id", productId);
      body.append("cc_type_limit", updates.creditLimit || "");
      body.append("cd_type_limit", updates.creditLimitControl || "2");
      // Add other fields as needed
      if (updates.creditDays) body.append("credit_days", updates.creditDays);
      if (updates.creditDaysControl) body.append("credit_days_control", updates.creditDaysControl);

      const response = await fetch(baseUrl + "equipment_master.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();

      if (!response.ok) {
        throw new Error(res.message || 'Failed to update product');
      }

      if (!res.success) {
        return rejectWithValue(res.message || 'API returned unsuccessful response');
      }

      return { productId, updates, response: res };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating equipment status (approve/reject)
export const updateEquipmentStatus = createAsyncThunk(
  "equipment/updateEquipmentStatus",
  async ({ equipmentId, userid, productIds, status, updates = {} }, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "update_status");
      body.append("id", equipmentId);
      body.append("userid", userid);

      productIds.forEach((productId) => {
        const productUpdates = updates[productId] || {};

        // 🔹 Send as single values (NOT arrays)
        body.append("details_id", productId);
        body.append("status", status);
        body.append(
          "cc_type_limit",
          productUpdates.cc_type_limit || productUpdates.creditLimitControl || "1"
        );
        body.append(
          "cd_type_limit",
          productUpdates.cd_type_limit || productUpdates.debitLimitControl || "2"
        );
        body.append("quantity", productUpdates.quantity || "1");

        // 🔹 Special handling for vendor_id → send as array
        if (productUpdates.vendor_id && Array.isArray(productUpdates.vendor_id)) {
          productUpdates.vendor_id.forEach((vendorId, vendorIndex) => {
            body.append(`vendor_id[${vendorIndex}]`, vendorId);
          });
        } else if (productUpdates.vendor_id) {
          body.append(`vendor_id[0]`, productUpdates.vendor_id);
        } else {
          body.append(`vendor_id[0]`, "");
        }
      });

      const response = await fetch(baseUrl + "equipment_master.php", {
        method: "POST",
        body: body,
      });

      const res = await response.json();

      if (!response.ok) throw new Error(res.message || "Failed to update equipment status");
      if (!res.success) return rejectWithValue(res.message || "API returned unsuccessful response");

      return { productIds, status, updates, response: res };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Helper function to get status text
const getStatusText = (status) => {
  switch (String(status)) {
    case "0":
      return "Under Review";
    case "1":
      return "Rejected";
    case "2":
      return "Approved";
    default:
      return "Under Review";
  }
};

const equipmentSlice = createSlice({
  name: "equipment",
  initialState: {
    data: [],
    details: null,
    loading: false,
    detailsLoading: false,
    updateLoading: false,
    error: null,
    detailsError: null,
    updateError: null,
    currentPage: 1,
    totalPages: 1,
    totalcount: 0,
  },
  reducers: {
    clearEquipmentData: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
    clearEquipmentDetails: (state) => {
      state.details = null;
      state.detailsLoading = false;
      state.detailsError = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    // Local update for optimistic updates
    updateEquipmentStatusLocal: (state, action) => {
      const { equipmentId, productIds, status, updates } = action.payload;

      // Update the details object if it's currently being viewed
      if (state.details && state.details.id === equipmentId) {
        if (state.details.details && state.details.details.length > 0) {
          state.details.details = state.details.details.map(detail => {
            if (productIds.includes(detail.id)) {
              return {
                ...detail,
                status: status,
                cc_type_limit: updates[detail.id]?.creditLimit || updates[detail.id]?.cc_type_limit || detail.cc_type_limit,
                cd_type_limit: updates[detail.id]?.creditLimitControl || updates[detail.id]?.cd_type_limit || detail.cd_type_limit,
                datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                // Add a log entry for the update
                logs: [
                  ...(detail.logs || []),
                  {
                    id: Date.now().toString(),
                    ref_id: detail.id,
                    status: status,
                    prod_type: detail.prod_type,
                    prod_code: detail.prod_code,
                    product_name: detail.product_name,
                    cc_type_limit: updates[detail.id]?.creditLimit || updates[detail.id]?.cc_type_limit || detail.cc_type_limit,
                    cd_type_limit: updates[detail.id]?.creditLimitControl || updates[detail.id]?.cd_type_limit || detail.cd_type_limit,
                    datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                    statustext: getStatusText(status)
                  }
                ]
              };
            }
            return detail;
          });
        }
      }

      // Also update the main data array
      state.data = state.data.map(equipment => {
        if (equipment.id === equipmentId && equipment.details) {
          return {
            ...equipment,
            details: equipment.details.map(detail => {
              if (productIds.includes(detail.id)) {
                return {
                  ...detail,
                  status: status,
                  cc_type_limit: updates[detail.id]?.creditLimit || updates[detail.id]?.cc_type_limit || detail.cc_type_limit,
                  cd_type_limit: updates[detail.id]?.creditLimitControl || updates[detail.id]?.cd_type_limit || detail.cd_type_limit,
                  datetime: new Date().toISOString().replace('T', ' ').substring(0, 19)
                };
              }
              return detail;
            })
          };
        }
        return equipment;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch equipment data
      .addCase(fetchEquipmentData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEquipmentData.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;

        if (data.success) {
          state.data = data.data || [];
          state.totalcount = parseInt(data.totalcount) || state.data.length;
          state.totalPages = Math.ceil(state.totalcount / 10);
        } else {
          state.error = data.message || "Failed to fetch equipment data";
          state.data = [];
        }
      })
      .addCase(fetchEquipmentData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.data = [];
      })

      // Fetch equipment details
      .addCase(fetchEquipmentDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchEquipmentDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        const data = action.payload;

        if (data.success) {
          state.details = Array.isArray(data.data) ? data.data[0] : data.data;
        } else {
          state.detailsError = data.message || "Failed to fetch equipment details";
          state.details = null;
        }
      })
      .addCase(fetchEquipmentDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload || action.error.message;
        state.details = null;
      })

      // Update equipment product - FIXED: Preserve original status
      .addCase(updateEquipmentProduct.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateEquipmentProduct.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { productId, updates } = action.payload;
      
        if (state.details && state.details.details) {
          state.details.details = state.details.details.map(detail => {
            if (detail.id === productId) {
              // ✅ CRITICAL FIX: Preserve the original status, don't change it
              const originalStatus = detail.status; // Keep the original status
              
              return {
                ...detail,
                // ✅ Keep the original status unchanged
                status: originalStatus,
                cc_type_limit: updates.creditLimit || detail.cc_type_limit,
                cd_type_limit: updates.creditLimitControl || detail.cd_type_limit,
                datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                logs: [
                  ...(detail.logs || []),
                  {
                    id: Date.now().toString(),
                    ref_id: detail.id,
                    status: originalStatus, // ✅ Use original status in log
                    prod_type: detail.prod_type,
                    prod_code: detail.prod_code,
                    product_name: detail.product_name,
                    cc_type_limit: updates.creditLimit || detail.cc_type_limit,
                    cd_type_limit: updates.creditLimitControl || detail.cd_type_limit,
                    datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                    // ✅ CRITICAL FIX: Use original status text, not "Updated"
                    statustext: getStatusText(originalStatus)
                  }
                ]
              };
            }
            return detail;
          });
        }

        // ✅ Also update the main data array with preserved status
        state.data = state.data.map(equipment => {
          if (equipment.details) {
            return {
              ...equipment,
              details: equipment.details.map(detail => {
                if (detail.id === productId) {
                  return {
                    ...detail,
                    // ✅ Keep original status unchanged
                    status: detail.status,
                    cc_type_limit: updates.creditLimit || detail.cc_type_limit,
                    cd_type_limit: updates.creditLimitControl || detail.cd_type_limit,
                    datetime: new Date().toISOString().replace('T', ' ').substring(0, 19)
                  };
                }
                return detail;
              })
            };
          }
          return equipment;
        });
      })
      
      .addCase(updateEquipmentProduct.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || action.error.message;
      })

      // Update equipment status
      .addCase(updateEquipmentStatus.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateEquipmentStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { productIds, status, updates } = action.payload;

        // Update the details object if it's currently being viewed
        if (state.details && state.details.details) {
          state.details.details = state.details.details.map(detail => {
            if (productIds.includes(detail.id)) {
              const productUpdates = updates[detail.id] || {};
              return {
                ...detail,
                status: status, // ✅ Only change status when explicitly updating status
                cc_type_limit: productUpdates.creditLimit || productUpdates.cc_type_limit || detail.cc_type_limit,
                cd_type_limit: productUpdates.creditLimitControl || productUpdates.cd_type_limit || detail.cd_type_limit,
                datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                // Add a log entry for the update
                logs: [
                  ...(detail.logs || []),
                  {
                    id: Date.now().toString(),
                    ref_id: detail.id,
                    status: status,
                    prod_type: detail.prod_type,
                    prod_code: detail.prod_code,
                    product_name: detail.product_name,
                    cc_type_limit: productUpdates.creditLimit || productUpdates.cc_type_limit || detail.cc_type_limit,
                    cd_type_limit: productUpdates.creditLimitControl || productUpdates.cd_type_limit || detail.cd_type_limit,
                    datetime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                    statustext: getStatusText(status)
                  }
                ]
              };
            }
            return detail;
          });
        }

        // Also update the main data array
        state.data = state.data.map(equipment => {
          if (equipment.details) {
            return {
              ...equipment,
              details: equipment.details.map(detail => {
                if (productIds.includes(detail.id)) {
                  const productUpdates = updates[detail.id] || {};
                  return {
                    ...detail,
                    status: status, // ✅ Only change status when explicitly updating status
                    cc_type_limit: productUpdates.creditLimit || productUpdates.cc_type_limit || detail.cc_type_limit,
                    cd_type_limit: productUpdates.creditLimitControl || productUpdates.cd_type_limit || detail.cd_type_limit,
                    datetime: new Date().toISOString().replace('T', ' ').substring(0, 19)
                  };
                }
                return detail;
              })
            };
          }
          return equipment;
        });
      })
      .addCase(updateEquipmentStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload || action.error.message;
      });
  },
});

export const {
  clearEquipmentData,
  clearEquipmentDetails,
  setCurrentPage,
  updateEquipmentStatusLocal,
  clearUpdateError
} = equipmentSlice.actions;

export default equipmentSlice.reducer;